import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("loading"); // loading|success|error
  const [message, setMessage] = useState("");
  const nav = useNavigate();
  // F-114 — resend flow (only shown on error/expired state)
  const [resendEmail, setResendEmail] = useState("");
  const [resendState, setResendState] = useState("idle"); // idle|sending|sent

  const resendVerify = async () => {
    if (!resendEmail || !/.+@.+\..+/.test(resendEmail)) return;
    setResendState("sending");
    try {
      await api.post("/auth/resend-verify", { email: resendEmail });
    } catch { /* swallow — always show the same success copy */ }
    setResendState("sent");
  };

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage("Missing verification token"); return; }
    api.post("/auth/verify-email", { token })
      .then((r) => { setStatus("success"); setMessage(r.data.email || ""); })
      .catch((e) => { setStatus("error"); setMessage(e.response?.data?.detail || "Verification failed"); });
  }, [token]);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',system-ui,sans-serif", padding: 20 }} data-testid="verify-email-page">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap')`}</style>
      <div style={{ background: "#fff", borderRadius: 16, padding: "44px 36px", maxWidth: 460, width: "100%", textAlign: "center", boxShadow: "0 4px 40px rgba(0,0,0,0.08)" }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: T.navy, marginBottom: 18 }}>
          WaveScore<span style={{ color: T.green }}>Pro</span>
        </div>
        {status === "loading" && <div data-testid="verify-loading" style={{ color: T.muted }}>Verifying your email…</div>}
        {status === "success" && (
          <div data-testid="verify-success">
            <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
            <h2 style={{ color: T.navy, fontSize: 20, fontWeight: 800, marginBottom: 6 }}>Email verified</h2>
            <p style={{ color: T.muted, fontSize: 14 }}>{message} is now confirmed. You're all set.</p>
            <button data-testid="verify-continue" onClick={() => nav("/login")}
              style={{ marginTop: 20, padding: "11px 26px", background: T.green, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
              Sign in →
            </button>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-error">
            <div style={{ fontSize: 56, marginBottom: 12 }}>⚠</div>
            <h2 style={{ color: T.navy, fontSize: 20, fontWeight: 800, marginBottom: 6 }}>Verification failed</h2>
            <p style={{ color: T.muted, fontSize: 14 }}>{message}</p>

            {/* F-114 — resend link */}
            {resendState !== "sent" ? (
              <div data-testid="verify-resend-form" style={{ marginTop: 22, textAlign: "left",
                background: "#F8FAFC", border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 8, lineHeight: 1.5 }}>
                  Need a new link? Enter your email — we&apos;ll resend the verification link (valid 24h).
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input data-testid="verify-resend-email" type="email"
                    placeholder="you@company.com" value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    style={{ flex: 1, padding: "10px 12px", background: "#fff",
                      border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 13,
                      color: T.navy, fontFamily: "inherit", outline: "none" }} />
                  <button data-testid="verify-resend-submit" onClick={resendVerify}
                    disabled={resendState === "sending" || !resendEmail}
                    style={{ padding: "10px 16px", background: T.green, color: "#fff",
                      border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
                      cursor: resendEmail ? "pointer" : "not-allowed", opacity: resendEmail ? 1 : 0.5 }}>
                    {resendState === "sending" ? "Sending…" : "Resend link"}
                  </button>
                </div>
              </div>
            ) : (
              <div data-testid="verify-resend-sent" style={{ marginTop: 22, padding: "12px 14px",
                background: "#F0FDF4", border: `1px solid #86EFAC`, borderRadius: 8,
                fontSize: 12, color: "#166534" }}>
                ✓ If that email is registered and unverified, a new link is on its way. Check your inbox.
              </div>
            )}

            <button data-testid="verify-retry" onClick={() => nav("/login")}
              style={{ marginTop: 20, padding: "11px 26px", background: "#fff", color: T.navy, border: `2px solid ${T.border}`, borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
